import type {
  BodyweightSource,
  LoadType,
  Modality,
  SessionStatus,
  SetType,
} from './enums.js';

/**
 * Plain data structures the domain logic operates on.
 *
 * These mirror the Phase 2 schema but are **not** database rows: they carry no
 * ORM types, no relations and no identity beyond plain string ids. That is what
 * lets the same functions run on the PC (fed by SQLite) and on the iPhone (fed
 * by an IndexedDB cache) and produce identical results.
 *
 * Nothing in `@gym/core` ever fetches these. They arrive as arguments.
 */

/** One logged set, primary or drop stage. Mirrors `session_set`. */
export interface LoggedSet {
  id: string;
  sessionExerciseId: string;
  setIndex: number;
  /** 0 for a primary set; 1+ for a drop stage. */
  dropIndex: number;
  setType: SetType;
  /** Null for a primary set; the primary set's id for a drop stage. */
  parentSetId: string | null;

  loadType: LoadType;
  weightG: number | null;
  bodyweightG: number | null;
  bodyweightSource: BodyweightSource;
  addedWeightG: number | null;
  assistWeightG: number | null;

  reps: number | null;
  durationS: number | null;
  rpe: number | null;

  isCompleted: boolean;
  completedAt: number | null;
}

/** One cardio segment. Mirrors `session_cardio_segment`. */
export interface CardioSegment {
  id: string;
  sessionExerciseId: string;
  segmentIndex: number;
  durationS: number | null;
  distanceM: number | null;
  calories: number | null;
  avgHr: number | null;
  maxHr: number | null;
  inclinePct: number | null;
  resistanceLevel: number | null;
  isCompleted: boolean;
}

/** Prescription for one set, already normalised to per-set form. */
export interface PrescribedSet {
  setIndex: number;
  dropIndex: number;
  setType: SetType;
  repMin: number | null;
  repMax: number | null;
  targetWeightG: number | null;
  targetDurationS: number | null;
  /**
   * Metres. Cardio Phase C1.
   *
   * `targetDurationS` alone expresses "run for 20 minutes" but cannot
   * express "run 5 km" — the two are genuinely different prescriptions, and
   * a cardio plan that can only state one of them is not usable. This is the
   * only field the cardio foundation adds; pace and speed are always derived
   * (`cardio.ts`), never stored, so they cannot drift from the values they
   * are computed from.
   */
  targetDistanceM: number | null;
}

/** An exercise as performed within one session. */
export interface PerformedExercise {
  sessionExerciseId: string;
  sessionId: string;
  exerciseId: string;
  modality: Modality;
  /** Session start, epoch milliseconds UTC. Used for recency ordering. */
  sessionStartedAt: number;
  sets: LoggedSet[];
  /**
   * Cardio Phase C7. Optional, same precedent as `DetectRecordsInput.segments`
   * (personal-records.ts) — one shared envelope, read by whichever fold
   * actually cares about it. `deriveExerciseTrend` (strength/hold) ignores
   * this; `deriveCardioTrend` reads only this and ignores `sets`. A cardio
   * exercise has zero `sets` by construction since C3, so the two folds
   * never see meaningful data from the field they don't handle.
   */
  segments?: readonly CardioSegment[];
}

/** Minimal session facts the domain logic needs. */
export interface SessionSnapshot {
  id: string;
  status: SessionStatus;
  startedAt: number;
  endedAt: number | null;
  /** 'YYYY-MM-DD' in the timezone the workout actually happened in. */
  localDate: string;
  /** Minutes offset from UTC at the time of the session. */
  tzOffsetMin: number;
}

/**
 * A result that either carries a value or explains why it could not be
 * produced. Used wherever "unknown" is a legitimate outcome that must not be
 * silently coerced to zero.
 */
export type Known<T> = { known: true; value: T };
export type Unknown<R extends string> = { known: false; reason: R };
export type Maybe<T, R extends string> = Known<T> | Unknown<R>;

export function known<T>(value: T): Known<T> {
  return { known: true, value };
}

export function unknown<R extends string>(reason: R): Unknown<R> {
  return { known: false, reason };
}
